import React, { useEffect, useMemo } from 'react';
import EditorBaseTheme from '../DaEditor/components/BaseTheme';
import isEqual from 'react-fast-compare';
import { DndProvider } from 'react-dnd-cjs';
import HTML5Backend from 'react-dnd-html5-backend-cjs';
import Notifications from '../Notifications';
import LiveNotifications from '../LiveNotifications';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import Modals from '../Modals';
import Head from './Head';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import { ThemeContext, ThemeSkinContext } from '@wix/da-react-context/pkg/ThemeContext';
import { MatureContext } from '@wix/da-react-context/pkg/MatureContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { HeadingLevelContext } from '@wix/da-a11y/pkg/components/headings/context';
import { UniqueIdContext, makeUniqueIdSource } from '@wix/da-hooks/pkg/useUniqueId';
import { FirstTimeFlag } from '../redux/firstTimeFlags';
import VerificationPopup from '../VerificationPopup';
import DuperbrowsePageWrapper from '../Duperbrowse/DuperbrowsePageWrapper';
import { BrowserFeatureContext } from '@wix/da-react-context/pkg/BrowserFeatureContext';
import { NCContext } from '@wix/da-react-context/pkg/NCContext';
import { usePageVisibility } from '@wix/da-hooks/pkg/usePageVisibility';
import { ScreenSizeContextProvider } from '../Measured';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import ChatInit from '../Chat/ChatInit';
import DRE from '../DRE';
import HoistCss from './HoistCss';
import TimeTravel from '../TimeTravel';
const ShimDndProvider = DndProvider;
/**
 * This is designed to wrap your render() output in App.tsx
 * it provides the common components that we need for some
 * shared things.
 *
 * Things it includes:
 *
 * - <Notifications /> (for general system notifications when deleting, adding to groups, faving, etc)
 * - <LiveNotifications /> (for real time notifications pulled from hub)
 * - <Modals />
 *
 * The dream
 * This component wraps all the header/context etc but currently
 * there is some variation between projects, so for now its limited
 * in its scope.
 */ const DaApp = ({ appMounted, appUnmounted, appVisibilityChange, modalsRenderFunc, showMatureContent = true, isMobile = false, theme, themeSkin, language, environment, bodyClassName, isDuperbrowseEnabled = true, isDuperbrowseActive, isLegacyHeader, supportsWebp = false, supportsLazy = false, publicSession, biPageContextData, disableHeadTags, isLoggedIn, firstTimeFlags, children, notificationsRenderFunc, notificationsWrapperClassName, disablePageWideNotifications, disableChat: disableChatProp, disableFloatingChat, disableSSRPopups, flags, duperbrowseViewType, isIframe, appUrl, ncContextProps = {} })=>{
    useEffect(()=>{
        appMounted();
        return appUnmounted;
    }, [
        appMounted,
        appUnmounted
    ]);
    usePageVisibility(appVisibilityChange);
    const browserSupport = useMemo(()=>({
            supportsWebp,
            supportsLazy
        }), [
        supportsLazy,
        supportsWebp
    ]);
    const uniqueIdSource = useMemo(()=>makeUniqueIdSource('app-root'), []);
    const ncContextPropsMemo = useMemo(()=>ncContextProps, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        ncContextProps.DrawerComponent,
        ncContextProps.PageComponent,
        ncContextProps.isStandalonePage
    ]);
    const renderPageContent = ()=>{
        return /*#__PURE__*/ React.createElement(React.Fragment, null, children, !disablePageWideNotifications && /*#__PURE__*/ React.createElement(Notifications, {
            wrapperClassName: notificationsWrapperClassName
        }, notificationsRenderFunc), /*#__PURE__*/ React.createElement(LiveNotifications, null));
    };
    const renderSSRPopups = ()=>{
        const unverified = isLoggedIn && !publicSession.isVerified;
        const hasNotSeenVerificationPopup = firstTimeFlags[FirstTimeFlag.WelcomePopupNotClosed];
        if (unverified && hasNotSeenVerificationPopup) {
            return /*#__PURE__*/ React.createElement(VerificationPopup, {
                dropFlagOnClose: true
            });
        }
    };
    const disableChat = flags.includes('chat_to_notes') && disableChatProp !== false;
    // the error boundary here is really a last ditch effort - if the error
    // got here the entire page is dead already so we're just saving face
    return /*#__PURE__*/ React.createElement(ErrorBoundary, {
        debugComponent: "DaApp"
    }, /*#__PURE__*/ React.createElement(SessionContext.Provider, {
        value: publicSession
    }, !disableHeadTags && /*#__PURE__*/ React.createElement(Head, {
        environment: environment,
        language: language,
        theme: theme,
        bodyClassName: bodyClassName,
        themeSkin: themeSkin
    }), /*#__PURE__*/ React.createElement(EditorBaseTheme, null), /*#__PURE__*/ React.createElement(HoistCss, null), /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
        value: biPageContextData
    }, /*#__PURE__*/ React.createElement(BrowserFeatureContext.Provider, {
        value: browserSupport
    }, /*#__PURE__*/ React.createElement(MatureContext.Provider, {
        value: showMatureContent
    }, /*#__PURE__*/ React.createElement(MobileContext.Provider, {
        value: isMobile
    }, /*#__PURE__*/ React.createElement(HeadingLevelContext.Provider, {
        value: 1
    }, /*#__PURE__*/ React.createElement(UniqueIdContext.Provider, {
        value: uniqueIdSource
    }, /*#__PURE__*/ React.createElement(ShimDndProvider, {
        backend: HTML5Backend
    }, /*#__PURE__*/ React.createElement(ThemeContext.Provider, {
        value: theme
    }, /*#__PURE__*/ React.createElement(ThemeSkinContext.Provider, {
        value: themeSkin
    }, /*#__PURE__*/ React.createElement(ScreenSizeContextProvider, null, /*#__PURE__*/ React.createElement(NCContext.Provider, {
        value: ncContextPropsMemo
    }, isDuperbrowseEnabled ? /*#__PURE__*/ React.createElement(DuperbrowsePageWrapper, {
        viewType: duperbrowseViewType
    }, renderPageContent()) : renderPageContent(), /*#__PURE__*/ React.createElement(Modals, null, modalsRenderFunc), !disableSSRPopups && renderSSRPopups(), !disableChat && /*#__PURE__*/ React.createElement(ChatInit, {
        disableFloatingChat: disableFloatingChat
    }), flags.includes('dre') && !isLegacyHeader && /*#__PURE__*/ React.createElement(DRE, {
        environment: environment
    }), flags.includes('time_travel') && !isLegacyHeader && /*#__PURE__*/ React.createElement(TimeTravel, null))))))))))))));
};
DaApp.displayName = 'DaApp';
export default /*#__PURE__*/ React.memo(DaApp, isEqual);
